import { useEffect, useState } from 'react';
import { searchNearby } from '@/api/places';
import type { ItineraryItem } from '@/types/itinerary';

// Background-ingest polling: re-query while the server reports `pending` so
// results keep filling in. A large radius ingests thousands of places over a
// few minutes, so back the interval off (3s → 15s) across up to N polls
// (~3 min total) rather than giving up after a few seconds. Bounded so a
// permanently under-covered area (a rate-limited provider) still settles.
// Attempts 0..MAX_SEARCH_POLLS run inclusive: delays sum to ~183s over 16
// fetches (48s ramp for attempts 0–5, then 15s × 9).
const MAX_SEARCH_POLLS = 15;
const searchPollDelayMs = (attempt: number): number => Math.min(3000 + attempt * 2000, 15000);

interface PlacesSearchParams {
  lat: number | null;
  lng: number | null;
  radiusMiles: number;
  /**
   * FSQ-only supplemental subcategory selection. When provided, it's forwarded
   * to the worker and changes refire the search. Pass `null` to use the
   * server-side defaults (e.g., before the local selection has loaded).
   */
  supplementalCategoryIds?: Array<string> | null;
  /**
   * Bumping this nonce re-runs the search even when other inputs are unchanged.
   * Used by the refresh-to-invalidate flow: after deleting the cache server-
   * side, increment the nonce so the next render's effect refires.
   */
  refreshNonce?: number;
}

interface PlacesSearchResult {
  items: Array<ItineraryItem>;
  /** Initial fetch, before any results are in — drives the list skeleton.
   *  Drops as soon as the first results arrive, even if more are still filling
   *  in behind the scenes. */
  isLoading: boolean;
  /** The server still reports `pending` and we're still polling for more —
   *  i.e. background ingest / photo enrichment is ongoing. Stays true through
   *  the whole fill-in window (after `isLoading` has dropped), so callers can
   *  show a "still working" affordance without holding the skeleton. */
  isSettling: boolean;
  error: string | null;
}

export const usePlacesSearch = ({
  lat,
  lng,
  radiusMiles,
  supplementalCategoryIds,
  refreshNonce = 0,
}: PlacesSearchParams): PlacesSearchResult => {
  const [items, setItems] = useState<Array<ItineraryItem>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettling, setIsSettling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // `refreshNonce` isn't read inside the body — it's a sentinel whose change
    // refires this effect. Reference it via void so the linter knows we mean it
    // to be in the dep array.
    void refreshNonce;

    if (lat === null || lng === null) {
      setItems([]);
      setIsSettling(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    setIsLoading(true);
    setIsSettling(true);
    setError(null);

    // The server serves cached places immediately and ingests missing cells in
    // the background, returning `pending: true` while more may still arrive.
    // Poll a bounded number of times so cold areas fill in without blocking.
    const run = (attempt: number): void => {
      searchNearby({
        lat,
        lng,
        radiusMeters: Math.round(radiusMiles * 1609.344),
        ...(supplementalCategoryIds ? { supplementalCategoryIds } : {}),
      })
        .then(({ items: results, pending }) => {
          if (cancelled) return;
          setItems(results);
          const keepPolling = pending && attempt < MAX_SEARCH_POLLS;
          // Drop the skeleton as soon as any results are in (or ingestion has
          // settled) — don't hold it through the whole background-fill window.
          if (results.length > 0 || !keepPolling) setIsLoading(false);
          // …but keep `isSettling` true while we're still polling, so the map's
          // search-area chip shows a spinner the whole time ingest/enrichment
          // is still filling in behind the already-visible results.
          setIsSettling(keepPolling);
          if (keepPolling) timer = setTimeout(() => run(attempt + 1), searchPollDelayMs(attempt));
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Search failed');
          setIsLoading(false);
          setIsSettling(false);
        });
    };
    run(0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lat, lng, radiusMiles, supplementalCategoryIds, refreshNonce]);

  return { items, isLoading, isSettling, error };
};
